import { Alert, Input, Select, Spin } from 'antd';
import { Profile } from 'oidc-client';
import React, { useEffect, useMemo, useState } from 'react';
import { defaultSelectedUserRole, getUserRole } from '../../helpers/CheckPermissionHelper';
import { includesForSearch, normalizeForSearch } from '../../helpers/normalizeForSearch';
import { MatchVm } from '../../utils/api';
import './insights.css';
import { canAccessInsights, canUseInsightsPages } from './insightsAccess';
import { useInsightsClub } from './insightsClubScope';
import { isFinishedMatch, ourTeamIds } from './insightsClubSummary';
import InsightsLegend from './InsightsLegend';
import {
    collectBundleFilterOptions,
    emptyMatchBundleFilters,
    filterMatchBundles,
    MatchBundleFilterSelection,
} from './insightsMatchBundleFilters';
import InsightsNav from './InsightsNav';
import { COMPARE_METRICS } from './insightsPlayerSplits';
import { fetchClubMatches, loadMatchBundles, MatchBundle } from './insightsQueries';
import InsightsScopeFilters from './InsightsScopeFilters';
import InsightsTopbar from './InsightsTopbar';
import {
    makeRateCell,
    RateMode,
    TwSavesBoard,
    TwScorersBoard,
    TwSupportExclusionsBoard,
} from './InsightsTwBoards';
import { buildTwPlayerBoards, TwPlayerBoardRow } from './insightsTwPlayerTables';
import InsightsValorizationBoard from './InsightsValorizationBoard';

interface Props {
    userProfile?: Profile;
}

const PAGE_SIZE = 100;

const InsightsPlayers: React.FC<Props> = ({ userProfile }) => {
    // getUserRole returns a new object every call; putting it in effect deps
    // retriggered the page fetch in a tight loop.
    const userRole = useMemo(
        () => (userProfile ? getUserRole(userProfile) : defaultSelectedUserRole),
        [userProfile]
    );
    const allowed = canAccessInsights(userRole);

    const scope = useInsightsClub(userProfile, allowed && canUseInsightsPages(userRole));
    const { selectedClubId, selectedTeamId } = scope;

    const [allBundles, setAllBundles] = useState<MatchBundle[]>([]);
    const [filters, setFilters] = useState<MatchBundleFilterSelection>(emptyMatchBundleFilters());
    const [search, setSearch] = useState('');
    const [minMp, setMinMp] = useState(1);
    const [rateMode, setRateMode] = useState<RateMode>('totals');
    const [compareIds, setCompareIds] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [fetchedTotal, setFetchedTotal] = useState(0);

    const teamIds = useMemo(
        () => ourTeamIds(scope.teams, selectedTeamId),
        [scope.teams, selectedTeamId]
    );

    useEffect(() => {
        let cancelled = false;

        const load = async (): Promise<void> => {
            if (
                !allowed ||
                !canUseInsightsPages(userRole) ||
                selectedClubId == null ||
                teamIds.length === 0
            ) {
                setAllBundles([]);
                setFetchedTotal(0);
                return;
            }

            setLoading(true);
            setError(undefined);
            try {
                const page = await fetchClubMatches({
                    clubId: selectedClubId,
                    teamId: selectedTeamId,
                    pageSize: PAGE_SIZE,
                });
                if (cancelled) return;

                const finished = (page.pageItems || []).filter((match: MatchVm) =>
                    isFinishedMatch(match)
                );
                const bundles = await loadMatchBundles(finished);
                if (cancelled) return;

                setAllBundles(bundles);
                setFetchedTotal(page.totalItems || finished.length);
                setFilters(emptyMatchBundleFilters());
            } catch {
                if (!cancelled) {
                    setAllBundles([]);
                    setFetchedTotal(0);
                    setError('Could not load player boards.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [allowed, userRole, selectedClubId, selectedTeamId, teamIds]);

    const filterOptions = useMemo(
        () => collectBundleFilterOptions(allBundles, teamIds),
        [allBundles, teamIds]
    );

    const filteredBundles = useMemo(
        () => filterMatchBundles(allBundles, filters),
        [allBundles, filters]
    );

    const boards = useMemo(
        () => buildTwPlayerBoards(filteredBundles, teamIds),
        [filteredBundles, teamIds]
    );

    const visible = useMemo(() => {
        const q = search.trim();
        return boards.filter((row: TwPlayerBoardRow) => {
            if (row.matches < minMp) return false;
            if (!q) return true;
            return (
                includesForSearch(row.name, q) ||
                row.capNumbers.some((c) => String(c).includes(normalizeForSearch(q)))
            );
        });
    }, [boards, search, minMp]);

    const cell = useMemo(() => makeRateCell(rateMode), [rateMode]);

    const scorers = useMemo(
        () => visible.filter((r) => r.shots.total.attempts > 0 || r.shots.total.goals > 0),
        [visible]
    );
    const keepers = useMemo(() => visible.filter((r) => r.saves.total.attempts > 0), [visible]);
    const supportExRows = useMemo(
        () =>
            visible.filter(
                (r) =>
                    r.assists > 0 ||
                    r.blocks > 0 ||
                    r.steals > 0 ||
                    r.turnovers > 0 ||
                    r.exclusionsDrawn > 0 ||
                    r.exclusionsCommitted > 0
            ),
        [visible]
    );

    const compareRows = useMemo(
        () =>
            compareIds
                .map((id) => boards.find((r) => r.playerId === id))
                .filter((r): r is TwPlayerBoardRow => r != null)
                .slice(0, 5),
        [compareIds, boards]
    );

    const visibleIds = useMemo(() => new Set(visible.map((r) => r.playerId)), [visible]);

    const shortfall =
        fetchedTotal > PAGE_SIZE
            ? `Showing latest ${PAGE_SIZE} finished matches of ${fetchedTotal} available.`
            : undefined;

    if (!canUseInsightsPages(userRole)) {
        return (
            <Alert
                type="warning"
                showIcon
                message="Insights is only on staging and the Statistics Portal"
            />
        );
    }

    if (!allowed) {
        return (
            <div className="insightsRoot">
                <InsightsTopbar />
                <div className="insightsBlocked">
                    <h1>Not available for this account</h1>
                    <p>Club Insights is for Federation and Club accounts that enter statistics.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="insightsRoot">
            <InsightsTopbar scope={scope} />
            <div className="insightsBody">
                <InsightsNav active="players" />
                <InsightsLegend />

                <InsightsScopeFilters
                    options={filterOptions}
                    filters={filters}
                    onChange={setFilters}
                    matchCount={filteredBundles.length}
                />

                <div className="insightsPlayersToolbar">
                    <div>
                        <label className="insightsFilterLabel">Search</label>
                        <Input
                            allowClear
                            placeholder="Name or cap"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="insightsFilterLabel">Min matches</label>
                        <Select
                            style={{ width: '100%' }}
                            value={minMp}
                            onChange={setMinMp}
                            options={[1, 2, 3, 5, 10].map((n) => ({ value: n, label: String(n) }))}
                        />
                    </div>
                    <div>
                        <label className="insightsFilterLabel">Rate</label>
                        <div className="insightsRateToggle">
                            <button
                                type="button"
                                className={rateMode === 'totals' ? 'is-active' : undefined}
                                onClick={() => setRateMode('totals')}
                            >
                                Totals
                            </button>
                            <button
                                type="button"
                                className={rateMode === 'perGame' ? 'is-active' : undefined}
                                onClick={() => setRateMode('perGame')}
                            >
                                Per game
                            </button>
                        </div>
                    </div>
                    <div className="insightsCompareSelect">
                        <label className="insightsFilterLabel">Compare (max 5)</label>
                        <Select
                            mode="multiple"
                            allowClear
                            placeholder="Pick players"
                            style={{ width: '100%', minWidth: 220 }}
                            value={compareIds}
                            onChange={(ids: number[]) => setCompareIds(ids.slice(0, 5))}
                            optionFilterProp="label"
                            options={boards.map((r) => ({
                                value: r.playerId,
                                label:
                                    r.capNumbers.length > 0
                                        ? `#${r.capNumbers[0]} ${r.name}`
                                        : r.name,
                            }))}
                        />
                    </div>
                </div>

                {error && (
                    <Alert type="error" showIcon message={error} style={{ marginBottom: 14 }} />
                )}
                {shortfall && <p className="insightsTableNote">{shortfall}</p>}

                <Spin spinning={loading || scope.loading}>
                    {filteredBundles.length === 0 && !loading ? (
                        <div className="insightsEmpty">
                            {allBundles.length === 0
                                ? 'No finished matches in this selection yet.'
                                : '0 matches for the current filters.'}
                        </div>
                    ) : (
                        <>
                            {compareRows.length >= 2 ? (
                                <section className="insightsPanel">
                                    <div className="insightsPanelHead">
                                        <h2>Player comparison</h2>
                                        <span>{compareRows.length} selected</span>
                                    </div>
                                    <table className="insightsPlayerTable insightsTableFit">
                                        <thead>
                                            <tr>
                                                <th>Metric</th>
                                                {compareRows.map((r) => (
                                                    <th key={r.playerId}>{r.name}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {COMPARE_METRICS.map((metric) => (
                                                <tr key={metric.key}>
                                                    <td>{metric.label}</td>
                                                    {compareRows.map((r) => (
                                                        <td key={r.playerId}>
                                                            {metric.pick(r)}
                                                            {metric.key === 'shooting' ||
                                                            metric.key === 'savePct'
                                                                ? typeof metric.pick(r) === 'number'
                                                                    ? '%'
                                                                    : ''
                                                                : ''}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </section>
                            ) : compareIds.length > 0 ? (
                                <p className="insightsTableNote">
                                    Select at least 2 players to compare.
                                </p>
                            ) : null}

                            <TwScorersBoard rows={scorers} cell={cell} rateMode={rateMode} />
                            <TwSavesBoard rows={keepers} cell={cell} rateMode={rateMode} />
                            <TwSupportExclusionsBoard rows={supportExRows} cell={cell} />
                            <InsightsValorizationBoard
                                bundles={filteredBundles}
                                ourTeamIds={teamIds}
                                visibleIds={visibleIds}
                            />
                        </>
                    )}
                </Spin>
            </div>
        </div>
    );
};

export default InsightsPlayers;
